import { CCAMService } from '@services/CCAMService'
import { CCAMDto } from '@services/dtos'
import { InfoOutlined } from '@mui/icons-material'
import {
  CircularProgress,
  Typography,
  Stack,
  Divider,
  Tooltip,
  Box,
} from '@mui/material'
import { FC, useState, useCallback } from 'react'

export const CcamDetailsTooltip: FC<{
  code: string
  examDate: Date
  cache: Map<string, CCAMDto>
}> = ({ code, examDate, cache }) => {
  const [open, setOpen] = useState(false)
  const [details, setDetails] = useState<CCAMDto | undefined>(() =>
    cache.get(code),
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const handleOpen = useCallback(async () => {
    setOpen(true)
    if (details || loading) return

    const cached = cache.get(code)
    if (cached) {
      setDetails(cached)
      return
    }

    setLoading(true)
    setError(false)
    try {
      const result = await CCAMService.get({ code, examDate })
      const first = Array.isArray(result) ? result[0] : result
      if (first) {
        cache.set(code, first)
        setDetails(first)
      } else {
        setError(true)
      }
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [code, examDate, cache, details, loading])

  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <Tooltip
      arrow
      placement="top"
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      title={
        <Box sx={{ p: 0.5, minWidth: 240 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={16} sx={{ color: 'grey.300' }} />
            </Box>
          )}
          {!loading && error && (
            <Typography variant="caption" sx={{ color: 'grey.300' }}>
              Détails indisponibles
            </Typography>
          )}
          {!loading && !error && details && (
            <Stack spacing={0.75}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: 'grey.300' }}>
                  Code
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {code}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'grey.300', display: 'block' }}
                >
                  Libellé
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {details.libelle}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: 'grey.300' }}>
                  Regroupement
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {details.codeRegroupement}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: 'grey.300' }}>
                  Prix OPTAM
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {details.prixOptam}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: 'grey.300' }}>
                  Prix non-OPTAM
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {details.prixNonOptam}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="caption" sx={{ color: 'grey.300' }}>
                  Modificateurs
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {details.modificateurs || '—'}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      }
    >
      <InfoOutlined
        fontSize="small"
        className="shrink-0 cursor-help text-gray-400 hover:text-gray-600"
        sx={{ fontSize: 16 }}
      />
    </Tooltip>
  )
}
